/* eslint-disable import/no-unresolved */
import 'swiper/css';
import 'swiper/css/free-mode';

import { useState } from 'react';
import { Lazy } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper/types';
import { trackEvent } from '@kcd/event-tracker';

import { useCurrentUser } from '~/domains/auth/queries';
import { PRODUCT_TYPE_E_COUPON } from '~/domains/e-coupon/constants';
import ECouponProductDisplayItem from '~/domains/products/ProductListDisplay/ECouponProductDisplayItem';
import { useTopPrCategory } from '~/domains/products/queries/useTopPvCategory';
import { useTopPvProducts } from '~/domains/products/queries/useTopPvProducts';
import PopularCategoryList from '~/domains/showcase/components/PopularCategoryList';
import { useEffectOnce } from '~/hooks/lifecycle';
import { useUpdateRouterQuery } from '~/hooks/url';
import type { OAISchema } from '~/types/openapi';

import PopularProductItem from './PopularProductItem';

const PopularProductList = () => {
  const { data } = useTopPrCategory();
  const { user } = useCurrentUser();
  const updateRouterQuery = useUpdateRouterQuery();
  const [swiper, setSwiper] = useState<SwiperClass>();

  useEffectOnce(() => {
    trackEvent('viewed 마켓/홈 listItem in 인기상품', {
      userId: user?.trackingId,
      buyerId: user?.id,
    });
  });

  const onCategoryClick = (
    category: OAISchema['TopPvProductsCategoryDto'],
    index: number,
  ) => {
    swiper?.slideTo(index);
    trackEvent('clicked 마켓/홈 category in 인기상품', {
      userId: user?.trackingId,
      buyerId: user?.id,
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      placementName: '홈/인기상품',
    });
  };

  const onSlideChange = (activeIndex: number) => {
    const { categoryId } = data[activeIndex];
    void updateRouterQuery({ categoryId: `${categoryId}` });
  };

  return (
    <section>
      <PopularCategoryList onClickCategory={onCategoryClick} />

      <Swiper
        lazy
        onInit={setSwiper}
        slidesPerView={1}
        modules={[Lazy]}
        className="z-0 mt-16 h-full"
        onActiveIndexChange={(changed) => onSlideChange(changed.activeIndex)}
      >
        {data.map((category) => {
          return (
            <SwiperSlide key={category.categoryId} className="z-0">
              <PopularProductSlide categoryId={category.categoryId} />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
};

export default PopularProductList;

function PopularProductSlide({ categoryId }: { categoryId: number }) {
  const { data } = useTopPvProducts(categoryId.toString());

  return (
    <div className="flex flex-col gap-16 px-20">
      {data?.products?.slice(0, 3).map((product, index) => {
        return (
          <div className="relative">
            {product.productType === PRODUCT_TYPE_E_COUPON ? (
              <ECouponProductDisplayItem
                product={product}
                key={product.offerId}
                reverseCouponDiscountedPrice
                index={index + 1}
              />
            ) : (
              <PopularProductItem
                key={product.offerId}
                product={product}
                index={index + 1}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
